import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UnifiedCampaign } from '@/types/unifiedCampaign';
import { useOptimizedSupabaseScripts } from '@/hooks/useOptimizedSupabaseScripts';
import { useSupabaseProspectLists } from '@/hooks/useSupabaseProspectLists';
import { useClients } from '@/hooks/useClients';
import { useVapiKeys } from '@/hooks/useVapiKeys';
import { useVoiceNames } from '@/hooks/useVoiceNames';
import { getVapiKeyFromCampaign } from '@/utils/campaignValidation';
import {
  Phone,
  PhoneIncoming,
  Calendar,
  MoreVertical,
  Play,
  Pause,
  Square,
  Trash2,
  Copy,
  Pencil,
  RotateCcw,
  SkipForward,
  BarChart2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface CampaignCallStats {
  [campaignId: string]: {
    totalCalls: number;
    conversions: number;
    transfers: number;
    messages: number;
    handRaised: number;
  };
}

interface UnifiedCampaignCardProps {
  campaign: UnifiedCampaign;
  campaignStats: CampaignCallStats;
  statsLoading: boolean;
  onStart: (id: string) => void;
  onPause: (id: string) => void;
  onStop: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onClone: (id: string) => void;
  onRestartFresh: (id: string) => void;
  onRestartResume: (id: string) => void;
  onRebuild?: (id: string) => void;
  onViewReport?: (id: string) => void;
}

export function UnifiedCampaignCard({
  campaign,
  campaignStats,
  statsLoading,
  onStart,
  onPause,
  onStop,
  onDelete,
  onEdit,
  onClone,
  onRestartFresh,
  onRestartResume,
  onRebuild,
  onViewReport,
}: UnifiedCampaignCardProps) {
  const { scripts } = useOptimizedSupabaseScripts();
  const { prospectLists } = useSupabaseProspectLists();
  const { clients } = useClients();
  const { getVapiFriendlyName, hasMultipleKeys } = useVapiKeys();
  const { getVoiceName } = useVoiceNames();

  // Get real-time stats for this campaign, only use after loading is complete
  const realTimeStats = !statsLoading ? campaignStats[campaign.id] : undefined;

  const displayStats = realTimeStats
    ? {
        totalCalls: realTimeStats.totalCalls,
        conversions: realTimeStats.conversions,
        transfers: realTimeStats.transfers,
        messages: realTimeStats.messages,
        handRaised: realTimeStats.handRaised,
      }
    : {
        totalCalls: campaign.stats.totalCalls || 0,
        conversions: campaign.stats.conversions || 0,
        transfers: campaign.stats.transfers || 0,
        messages: 0,
        handRaised: 0,
      };

  // Get script name
  const script = scripts.find(
    (s) =>
      s.id ===
      (campaign.script_id ||
        campaign.outbound_script_id ||
        campaign.inbound_script_id),
  );
  const scriptName = script?.name || 'No Script';

  // Get AI voice name from campaign.voice_id
  const agentName = useMemo(() => {
    if (!campaign.voice_id) return 'No Voice Set';
    const fromTable = getVoiceName(campaign.voice_id);
    if (fromTable !== 'Unknown Voice') return fromTable;
    // Fall back to shared ElevenLabs session cache (populated by wizard + edit dialog)
    try {
      const cached = sessionStorage.getItem('elevenlabs_voices');
      if (cached) {
        const voices = JSON.parse(cached);
        const voice = voices.find((v: any) => v.voice_id === campaign.voice_id);
        if (voice) return voice.name;
      }
    } catch {}
    return 'Unknown Voice';
  }, [campaign.voice_id, getVoiceName]);

  // Get VAPI key info for all campaigns when user has multiple keys
  const shouldShowVapiKey = hasMultipleKeys;
  const vapiKeyName = shouldShowVapiKey ? getVapiKeyFromCampaign(campaign) : '';
  const vapiFriendlyName = shouldShowVapiKey
    ? getVapiFriendlyName(vapiKeyName)
    : '';

  // Get client name if client_id exists
  const client = campaign.client_id
    ? clients.find((c) => c.id === campaign.client_id)
    : null;

  // Get prospect list name for outbound campaigns
  const prospectList = campaign.prospect_list_id
    ? prospectLists.find((pl) => pl.id === campaign.prospect_list_id)
    : null;
  const getCampaignIcon = (type: string) => {
    switch (type) {
      case 'inbound':
        return <PhoneIncoming className='h-4 w-4' />;
      case 'outbound_inbound':
        return (
          <div className='flex'>
            <Phone className='h-3 w-3' />
            <PhoneIncoming className='h-3 w-3' />
          </div>
        );
      default:
        return <Phone className='h-4 w-4' />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'paused':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20';
      case 'completed':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'cancelled':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      case 'draft':
        return 'bg-orange-500/15 text-orange-700 border-orange-500/30 font-semibold';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-500/20';
    }
  };

  return (
    <Card className='hover:shadow-md transition-shadow'>
      <CardHeader className='pb-3'>
        <div className='flex items-start justify-between'>
          <div className='flex items-start gap-2'>
            <div className='mt-0.5'>{getCampaignIcon(campaign.campaign_type)}</div>
            <div className='flex-1 min-w-0'>
              <CardTitle className='text-base font-semibold'>
                {campaign.name}
              </CardTitle>
              <p className='text-sm text-muted-foreground'>
                {campaign.description}
              </p>
              <div className='grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 mt-2 text-xs'>
                {client && (
                  <>
                    <span className='text-muted-foreground font-medium'>Client:</span>
                    <span className='truncate'>{client.name}</span>
                  </>
                )}
                <span className='text-muted-foreground font-medium'>Agent:</span>
                <span className='truncate'>{agentName}</span>
                <span className='text-muted-foreground font-medium'>Script:</span>
                <span className='truncate'>{scriptName}</span>
                {campaign.campaign_type !== 'inbound' && (
                  <>
                    <span className='text-muted-foreground font-medium'>List:</span>
                    <span className='truncate'>{prospectList ? prospectList.name : 'No List'}</span>
                  </>
                )}
                {shouldShowVapiKey && (
                  <>
                    <span className='text-muted-foreground font-medium'>VAPI Account:</span>
                    <span className='truncate'>{vapiFriendlyName}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm'>
                <MoreVertical className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {/* Start — draft, paused, or completed inbound */}
              {(campaign.status === 'draft' || campaign.status === 'paused' ||
                (campaign.status === 'completed' && campaign.campaign_type === 'inbound')) ? (
                <DropdownMenuItem onClick={() => onStart(campaign.id)}>
                  <Play className='h-4 w-4 mr-2' />
                  Start
                </DropdownMenuItem>
              ) : null}
              {/* Outbound restart options when completed */}
              {campaign.status === 'completed' && campaign.campaign_type !== 'inbound' ? (
                <>
                  <DropdownMenuItem onClick={() => onRestartFresh(campaign.id)}>
                    <RotateCcw className='h-4 w-4 mr-2' />
                    Restart from Beginning
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onRestartResume(campaign.id)}>
                    <SkipForward className='h-4 w-4 mr-2' />
                    Restart from Last Contact
                  </DropdownMenuItem>
                </>
              ) : null}
              {campaign.status === 'active' ? (
                <DropdownMenuItem onClick={() => onPause(campaign.id)}>
                  <Pause className='h-4 w-4 mr-2' />
                  Pause
                </DropdownMenuItem>
              ) : null}
              {campaign.status === 'active' || campaign.status === 'paused' ? (
                <DropdownMenuItem onClick={() => onStop(campaign.id)}>
                  <Square className='h-4 w-4 mr-2' />
                  Stop
                </DropdownMenuItem>
              ) : null}
              {onViewReport && campaign.status !== 'draft' && (
                <DropdownMenuItem onClick={() => onViewReport(campaign.id)}>
                  <BarChart2 className='h-4 w-4 mr-2' />
                  After Action Report
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onEdit(campaign.id)}>
                <Pencil className='h-4 w-4 mr-2' />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onClone(campaign.id)}>
                <Copy className='h-4 w-4 mr-2' />
                Clone
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(campaign.id)}
                className='text-destructive'
              >
                <Trash2 className='h-4 w-4 mr-2' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className='pt-0'>
        <div className='space-y-3'>
          <div className='flex items-center justify-between'>
            <Badge
              variant='outline'
              className={getStatusColor(campaign.status)}
            >
              {campaign.status}
            </Badge>
            <Badge variant='secondary'>
              {campaign.campaign_type.replace('_', ' & ')}
            </Badge>
          </div>

          <div className='text-sm text-muted-foreground space-y-1'>
            <div className='flex items-center gap-2'>
              <Phone className='h-3 w-3' />
              {campaign.phone_number || 'No phone assigned'}
            </div>
            <div className='flex items-center gap-2'>
              <Calendar className='h-3 w-3' />
              Created {campaign.created_at.toLocaleDateString()}
            </div>
          </div>

          {/* Campaign Stats Preview */}
          <div className='grid grid-cols-2 gap-2 text-xs'>
            <div>
              <span className='text-muted-foreground'>Calls:</span>{' '}
              {displayStats.totalCalls}
            </div>
            {campaign.campaign_type === 'inbound' ||
            campaign.campaign_type === 'outbound_inbound' ? (
              <div>
                <span className='text-muted-foreground'>Messages:</span>{' '}
                {displayStats.messages}
              </div>
            ) : (
              <div>
                <span className='text-muted-foreground'>Hands Raised:</span>{' '}
                {displayStats.handRaised}
              </div>
            )}
          </div>

          {campaign.tags && campaign.tags.length > 0 && (
            <div className='flex flex-wrap gap-1'>
              {campaign.tags.slice(0, 3).map((tag, index) => (
                <Badge key={index} variant='outline' className='text-xs'>
                  {tag}
                </Badge>
              ))}
              {campaign.tags.length > 3 && (
                <Badge variant='outline' className='text-xs'>
                  +{campaign.tags.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
